import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import {
  Home,
  Users,
  ShoppingBag,
  Package,
  Globe,
  FileText,
  CreditCard,
  Receipt,
  Hash,
  LayoutGrid,
  Table2,
  Utensils,
  ShoppingCart,
  Truck,
  Printer,
  Menu,
  X,
  Contact,
  PhoneIncoming,
  Tag,
  ClipboardList,
  Trash2,
  Building2,
  Palette,
  Wallet,
  ChefHat,
  Monitor
} from 'lucide-react';
import SupportContact from './../SupportContact';
import { useTheme } from '../../contexts/ThemeContext';

interface NavigationProps {
  activeTab: string;
  onTabClick: (tab: string) => void;
}

/**
 * Νέα εμφάνιση: πλαϊνό μενού σε ομάδες αντί για 2 σειρές με ~15 καρτέλες που δεν χωράνε
 * (έγκριση ιδιοκτήτη 11/10/2026). Ίδια στοιχεία, ίδιοι κανόνες ορατότητας — οι ομάδες
 * απλώς τα ταξινομούν. Ομάδα χωρίς ορατό στοιχείο δεν εμφανίζεται.
 *
 * 12/10/2026 (έγκριση ιδιοκτήτη): κάτοψη + τραπέζια + Dine-In → μία «Σάλα»· οι «Ρυθμίσεις» → ομάδα
 * «Κατάστημα» με τρεις σελίδες, και το «Ζ και Χ» στο Ταμείο· οι «Ρόλοι» μπαίνουν στους υπαλλήλους.
 * Φάση 3: «Προϊόντα» (η σελίδα /products) + «Υλικά» → ένας «Κατάλογος» μέσα στη διαχείριση.
 *
 * 13/10/2026 (ιδιοκτήτης, μετά το demo): η «Σάλα» ξανά τρεις επιλογές (Κάτοψη, Ανάθεση τραπεζιών,
 * Ρυθμίσεις Σάλας), χωρίς «Λογαριασμό», και η «Αρίθμηση παραστατικών» δική της επιλογή κάτω από τα
 * «Παραστατικά» (ως τότε στο τέλος των «Στοιχείων επιχείρησης»).
 */
export type NavGroupId = 'overview' | 'catalog' | 'people' | 'register' | 'operations' | 'store';

/** Οι καρτέλες της διαχείρισης καταστήματος. */
export type StoreTabId =
  | 'dashboard' | 'orders' | 'callhistory'
  | 'catalog' | 'dailydishes' | 'warehouse' | 'promotions' | 'platforms'
  | 'employees' | 'customers'
  | 'ecr' | 'numbering' | 'registerreports' | 'cashmovements' | 'pos' | 'printers' | 'devices' | 'waste'
  | 'floorplan' | 'tables' | 'salasettings' | 'takeawaysettings' | 'deliverysettings'
  | 'business' | 'appearance';

export const NAV_GROUPS: ReadonlyArray<{ id: NavGroupId; items: readonly StoreTabId[] }> = [
  { id: 'overview', items: ['dashboard', 'orders', 'callhistory'] },
  { id: 'catalog', items: ['catalog', 'dailydishes', 'warehouse', 'promotions', 'platforms'] },
  { id: 'people', items: ['employees', 'customers'] },
  { id: 'register', items: ['ecr', 'numbering', 'registerreports', 'cashmovements', 'pos', 'printers', 'devices', 'waste'] },
  { id: 'operations', items: ['floorplan', 'tables', 'salasettings', 'takeawaysettings', 'deliverysettings'] },
  { id: 'store', items: ['business', 'appearance'] },
];

const STORE_TABS: readonly string[] = NAV_GROUPS.flatMap(group => group.items);

// Καρτέλες που καταργήθηκαν: ζουν ακόμη στο localStorage (F5, σελιδοδείκτες της /products).
// Η «Σάλα» (12–13/10) → η κάτοψη, η πρώτη της καρτέλα· το «Dine-In» (ως 12/10) → οι ρυθμίσεις Σάλας.
const LEGACY_TABS = new Map<string, StoreTabId>([
  ['settings', 'business'],
  ['roles', 'employees'],
  ['sala', 'floorplan'],
  ['dineinsettings', 'salasettings'],
  ['account', 'business'],
  ['products', 'catalog'],
  ['ingredients', 'catalog'],
]);

/** Η καρτέλα που ανοίγει για ένα αποθηκευμένο id: ισχύον → το ίδιο, καταργημένο → η διάδοχός του, άγνωστο → «Αρχική». */
export function resolveStoreTab(id: string | null | undefined): StoreTabId {
  if (id && STORE_TABS.includes(id)) return id as StoreTabId;
  return (id && LEGACY_TABS.get(id)) || 'dashboard';
}

/** Οποιοσδήποτε τρόπος delivery (δικός μας, efood, Wolt) — τότε φαίνονται οι ρυθμίσεις Delivery. */
export const storeHasDelivery = (store?: { delivery?: boolean; delivery_self?: boolean; delivery_efood?: boolean; delivery_wolt?: boolean } | null) =>
  store?.delivery === true || store?.delivery_self === true || store?.delivery_efood === true || store?.delivery_wolt === true;

/** Παραστατικά μέσω Oxygen — τότε φαίνεται η «Αρίθμηση παραστατικών» (οι σειρές είναι του παρόχου). */
export const storeUsesOxygen = (store?: { ecr_provider?: string | null } | null) => store?.ecr_provider === 'oxygen';

/** Διαχειριστής καταστήματος (ή super admin): ο κανόνας των καρτελών που δεν βλέπει ο ταμίας. */
export const isStoreAdminRole = (employee?: { role?: string | null } | null) =>
  employee?.role === 'store_admin' || employee?.role === 'super_admin';

/** Τα ορατά στοιχεία ανά ομάδα. Στοιχείο που δεν ανήκει πουθενά (νέα καρτέλα) πάει στην τελευταία ομάδα — δεν χάνεται. */
export function groupNavItems<T extends { id: string }>(items: T[]): Array<{ id: NavGroupId; items: T[] }> {
  const byId = new Map(items.map(item => [item.id, item]));
  const placed = new Set<string>();
  const groups = NAV_GROUPS.map(group => {
    const list = group.items.map(id => byId.get(id)).filter((item): item is T => item !== undefined);
    list.forEach(item => placed.add(item.id));
    return { id: group.id, items: list };
  });
  const unplaced = items.filter(item => !placed.has(item.id));
  if (unplaced.length > 0) groups[groups.length - 1].items.unshift(...unplaced);
  return groups.filter(group => group.items.length > 0);
}

const StoreNavigation: React.FC<NavigationProps> = ({ activeTab, onTabClick }) => {
  const { t } = useTranslation(['settings', 'common', 'orders']);
  const { store, employee } = useAuth();
  const { look } = useTheme();
  const v2 = look === 'v2';
  const [mobileOpen, setMobileOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Close drawer on outside click
  useEffect(() => {
    if (!mobileOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [mobileOpen]);

  // Close drawer on escape key
  useEffect(() => {
    if (!mobileOpen) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMobileOpen(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [mobileOpen]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Νέα εμφάνιση: το πλαϊνό μενού έχει δικό του scroll και ξεκινά από πάνω σε κάθε φόρτωση
  // (π.χ. μετά από F5) — φέρε την ενεργή επιλογή στο μέσο αν δεν φαίνεται.
  const sideNavRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const nav = sideNavRef.current;
    const active = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !active) return;
    const navBox = nav.getBoundingClientRect();
    const box = active.getBoundingClientRect();
    if (box.top < navBox.top || box.bottom > navBox.bottom) {
      nav.scrollTop += box.top - navBox.top - (navBox.height - box.height) / 2;
    }
  }, [v2, activeTab]);

  const isAdmin = isStoreAdminRole(employee);
  const navigationItems = [
    { id: 'dashboard', label: t('settings:nav.dashboard'), icon: Home },
    // Orders management — the admin needs to reach every order (incl. completed)
    // to cancel it together with its receipt. Admin-only: cashiers use the POS
    // orders screen.
    ...(isAdmin
      ? [{ id: 'orders', label: t('orders:title', { defaultValue: 'Παραγγελίες' }), icon: ClipboardList }]
      : []),
    // Κλήσεις — ο ταμίας δεν φτάνει ποτέ σε αυτή τη σελίδα (τις βλέπει στο ταμείο).
    ...(isAdmin
      ? [{ id: 'callhistory', label: t('settings:nav.callHistory'), icon: PhoneIncoming }]
      : []),
    // «Κατάλογος»: προϊόντα, κατηγορίες, ομάδες επιλογών/μεγεθών, υλικά (components/catalog).
    { id: 'catalog', label: t('settings:nav.catalog'), icon: ShoppingBag },
    // Πιάτα ημέρας (migration 278): ο διαχειριστής — ΚΑΙ με κλειστό διακόπτη, γιατί ο διακόπτης ζει σε αυτή τη
    // σελίδα (όπως τα «Έντονα δελτία» στους «Εκτυπωτές»). Ο ταμίας τα βλέπει από το μενού του ταμείου.
    ...(isAdmin
      ? [{ id: 'dailydishes', label: t('settings:nav.dailyDishes'), icon: ChefHat }]
      : []),
    // Only show Warehouse for stores with warehouse mode enabled
    ...(store?.warehouse !== false
      ? [{ id: 'warehouse', label: t('settings:nav.warehouse'), icon: Package }]
      : []),
    ...(isAdmin
      ? [{ id: 'promotions', label: t('settings:nav.promotions', { defaultValue: 'Promotions' }), icon: Tag }]
      : []),
    // Πλατφόρμες — ίδιο φίλτρο με «Παραγγελίες»/«Προσφορές»: τα διαπιστευτήρια
    // και οι αντιστοιχίσεις προϊόντων (ΦΠΑ απόδειξης) είναι δουλειά διαχειριστή,
    // όχι ταμία.
    ...(isAdmin
      ? [{ id: 'platforms', label: t('settings:nav.platforms'), icon: Globe }]
      : []),
    { id: 'employees', label: t('settings:nav.employees'), icon: Users },
    { id: 'customers', label: t('settings:nav.customers'), icon: Contact },
    ...(isAdmin
      ? [{ id: 'ecr', label: t('settings:nav.ecrFiscal'), icon: FileText }]
      : []),
    // Αρίθμηση παραστατικών — μόνο με Oxygen· χωρίς όρο ρόλου, όπως όταν ζούσε στα «Στοιχεία επιχείρησης».
    ...(storeUsesOxygen(store)
      ? [{ id: 'numbering', label: t('settings:nav.numbering'), icon: Hash }]
      : []),
    // «Ζ και Χ» και οι σελίδες του «Κατάστημα»: ό,τι έδειχναν οι παλιές «Ρυθμίσεις» — χωρίς όρο.
    { id: 'registerreports', label: t('settings:nav.registerReports'), icon: Receipt },
    // Κινήσεις ταμείου (έξοδα/αναλήψεις/καταθέσεις, CSV λογιστή, κατηγορίες): ο διαχειριστής, μόνο με ανοιχτό
    // τον διακόπτη του καταστήματος (cash_movements_enabled), όπως η «Φύρα».
    ...(store?.cash_movements_enabled === true && isAdmin
      ? [{ id: 'cashmovements', label: t('settings:nav.cashMovements'), icon: Wallet }]
      : []),
    ...(isAdmin
      ? [{ id: 'pos', label: t('settings:nav.posTerminals'), icon: CreditCard }]
      : []),
    ...(isAdmin
      ? [{ id: 'printers', label: t('settings:nav.printers'), icon: Printer }]
      : []),
    // Συσκευές ταμείου (25/10/2026): οθόνη πελάτη + αναγνώριση κλήσης ανά PC, και ο διακόπτης της οθόνης
    // πελάτη (ως τότε στην «Εμφάνιση»). Ίδιος κανόνας με τους «Εκτυπωτές».
    ...(isAdmin
      ? [{ id: 'devices', label: t('settings:nav.devices'), icon: Monitor }]
      : []),
    // Waste (φύρα) — μόνο όταν το κατάστημα έχει ενεργή την καταγραφή
    ...(store?.waste_enabled === true && isAdmin
      ? [{ id: 'waste', label: t('settings:nav.waste'), icon: Trash2 }]
      : []),
    // Σάλα: τρεις επιλογές, μόνο σε καταστήματα με τραπέζια. Ανάθεση και ρυθμίσεις μόνο ο διαχειριστής
    // (ο κανόνας των παλιών «Τραπέζια»/«Dine-In»)· η κάτοψη θέλει μόνο τραπέζια.
    ...(store?.dine_in === true
      ? [{ id: 'floorplan', label: t('settings:nav.floorplan'), icon: LayoutGrid }]
      : []),
    ...(store?.dine_in === true && isAdmin
      ? [{ id: 'tables', label: t('settings:nav.tables'), icon: Table2 }]
      : []),
    ...(store?.dine_in === true && isAdmin
      ? [{ id: 'salasettings', label: t('settings:nav.salaSettings'), icon: Utensils }]
      : []),
    // TakeAway Settings - for stores with takeaway enabled
    ...(store?.takeaway === true
      ? [{ id: 'takeawaysettings', label: t('settings:nav.takeaway'), icon: ShoppingCart }]
      : []),
    // Delivery Settings - for stores with any delivery enabled
    ...(storeHasDelivery(store) && isAdmin
      ? [{ id: 'deliverysettings', label: t('settings:nav.delivery'), icon: Truck }]
      : []),
    { id: 'business', label: t('settings:nav.business'), icon: Building2 },
    { id: 'appearance', label: t('settings:nav.appearance'), icon: Palette },
  ];

  const activeItem = navigationItems.find(item => item.id === activeTab);
  const ActiveIcon = activeItem?.icon || Home;

  const handleItemClick = (tabId: string) => {
    onTabClick(tabId);
    setMobileOpen(false);
  };

  if (v2) {
    const groupLabels: Record<NavGroupId, string> = {
      overview: t('settings:nav.groups.overview'),
      catalog: t('settings:nav.groups.catalog'),
      people: t('settings:nav.groups.people'),
      register: t('settings:nav.groups.register'),
      operations: t('settings:nav.groups.operations'),
      store: t('settings:nav.groups.store'),
    };
    const groups = groupNavItems(navigationItems);

    // Ίδια λίστα για το πλαϊνό μενού και το συρτάρι του κινητού· στο κινητό ψηλότερες γραμμές για το δάχτυλο.
    const renderGroups = (where: 'side' | 'drawer', onSelect: (tabId: string) => void) => groups.map(group => {
      const headingId = `store-nav-${where}-${group.id}`;
      return (
        <div key={group.id} role="group" aria-labelledby={headingId}>
          {/* role="heading" αντί για <h2>: το RoleLight.css αλλάζει το letter-spacing των h1–h3 */}
          <div id={headingId} role="heading" aria-level={2} className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-t4">
            {groupLabels[group.id]}
          </div>
          <ul className="space-y-0.5">
            {group.items.map(item => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    data-tab={item.id}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={() => onSelect(item.id)}
                    className={`w-full flex items-center gap-2.5 px-3 rounded-lg text-left text-sm transition-colors ${
                      where === 'side' ? 'h-8' : 'h-11'
                    } ${
                      isActive
                        ? 'bg-brand-soft text-brand font-semibold'
                        : 'text-t2 font-medium hover:bg-surface-3 hover:text-t1'
                    }`}
                  >
                    <Icon className={`h-4 w-4 shrink-0 ${isActive ? 'text-brand' : 'text-t3'}`} />
                    <span className="truncate">{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      );
    });

    return (
      <>
        {/* ===== DESKTOP: πλαϊνό μενού σε ομάδες (νέα εμφάνιση) =====
            Η στήλη τεντώνεται σε όλο το ύψος της σελίδας (StoreAdminLayout: md:flex)· το εσωτερικό μένει
            κολλημένο πάνω, με δικό του scroll, και χωρά κάτω από την κεφαλίδα (h-14 + 1px περίγραμμα) ώστε
            το τηλέφωνο υποστήριξης να φαίνεται χωρίς κύλιση. */}
        <aside className="xv2-store-nav hidden md:block w-60 shrink-0 bg-surface-2 border-r border-line">
          <div className="sticky top-0 flex flex-col h-[calc(100vh-3.5rem-1px)]">
            <nav ref={sideNavRef} aria-label={t('common:navigation')} className="flex-1 min-h-0 overflow-y-auto px-2 py-4 space-y-4">
              {renderGroups('side', onTabClick)}
            </nav>
            <div className="shrink-0 border-t border-line px-3 py-3 space-y-3">
              {/* Τηλέφωνο υποστήριξης — πάντα ορατό (tap-to-call) */}
              <div className="px-1"><SupportContact variant="mini" /></div>
            </div>
          </div>
        </aside>

        {/* ===== MOBILE: μπάρα με την τρέχουσα σελίδα + συρτάρι (νέα εμφάνιση) ===== */}
        <div className="xv2-store-nav md:hidden bg-surface-2 border-b border-line sticky top-0 z-40">
          <div className="flex items-center justify-between px-4 h-12">
            <div className="flex items-center gap-2 min-w-0">
              <ActiveIcon className="h-4 w-4 shrink-0 text-brand" />
              <span className="text-sm font-semibold text-t1 truncate">{activeItem?.label || t('common:menu')}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <SupportContact variant="mini" />
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                aria-label={t('common:navigation')}
                className="w-9 h-9 rounded-lg border border-line text-t2 hover:bg-surface-3 flex items-center justify-center transition-colors active:scale-95"
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/40" />
            <div
              ref={drawerRef}
              className="absolute right-0 top-0 h-full w-72 max-w-[85vw] bg-surface-2 border-l border-line shadow-2xl flex flex-col"
              style={{ animation: 'slideInRight 0.2s ease-out' }}
            >
              <div className="flex items-center justify-between px-4 h-14 border-b border-line shrink-0">
                <span className="text-sm font-semibold text-t1">{t('common:navigation')}</span>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  aria-label={t('common:close', { defaultValue: 'Κλείσιμο' })}
                  className="w-9 h-9 rounded-lg text-t2 hover:bg-surface-3 flex items-center justify-center transition-colors active:scale-95"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav aria-label={t('common:navigation')} className="flex-1 overflow-y-auto px-2 py-4 space-y-4">
                {renderGroups('drawer', handleItemClick)}
              </nav>
            </div>
          </div>
        )}

        <style>{`
          @keyframes slideInRight {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
        `}</style>
      </>
    );
  }

  return (
    <>
      {/* ===== DESKTOP: horizontal tabs (hidden on mobile) ===== */}
      <div className="role-navigation hidden md:block bg-surface-1 border-b border-line sticky top-0 z-40">
        <div className="w-full px-4 flex items-center justify-between gap-3">
          <nav className="flex flex-wrap gap-1 overflow-x-auto">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button aria-current={isActive ? 'page' : undefined}
                  key={item.id}
                  onClick={() => onTabClick(item.id)}
                  className={`role-tab
                    flex items-center gap-2 px-4 py-3 border-b-2 transition-all whitespace-nowrap text-sm
                    ${isActive
                      ? 'border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-500/10'
                      : 'border-transparent text-t3 hover:text-t2 hover:bg-surface-3 dark:hover:bg-surface-2/50'
                    }
                  `}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>
          {/* Τηλέφωνο υποστήριξης — πάντα ορατό (tap-to-call) */}
          <div className="shrink-0"><SupportContact variant="mini" /></div>
        </div>
      </div>

      {/* ===== MOBILE: hamburger bar + slide-out drawer (hidden on desktop) ===== */}
      <div className="role-navigation md:hidden bg-surface-1 border-b border-line sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 h-12">
          <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
            <ActiveIcon className="h-4 w-4" />
            <span className="text-sm font-semibold">{activeItem?.label || t('common:menu')}</span>
          </div>
          <div className="flex items-center gap-2">
            <SupportContact variant="mini" />
            <button
              onClick={() => setMobileOpen(true)}
              className="w-9 h-9 rounded-lg bg-surface-2 hover:bg-surface-3 text-t2 flex items-center justify-center transition-all active:scale-95"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer overlay + panel */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          
          {/* Drawer */}
          <div
            ref={drawerRef}
            className="absolute right-0 top-0 h-full w-72 bg-surface-1 border-l border-line shadow-2xl flex flex-col animate-slide-in-right"
            style={{ animation: 'slideInRight 0.2s ease-out' }}
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-4 h-14 border-b border-line flex-shrink-0">
              <span className="text-sm font-bold text-t1">{t('common:navigation')}</span>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-9 h-9 rounded-lg bg-surface-2 hover:bg-surface-3 text-t2 flex items-center justify-center transition-all active:scale-95"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Drawer items */}
            <nav className="flex-1 overflow-y-auto py-2">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <button aria-current={isActive ? 'page' : undefined}
                    key={item.id}
                    onClick={() => handleItemClick(item.id)}
                    className={`role-tab
                      w-full flex items-center gap-3 px-4 py-3 text-left transition-all text-sm
                      ${isActive 
                        ? 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-l-3 border-blue-400' 
                        : 'text-t3 hover:bg-surface-3 dark:hover:bg-surface-2/50 hover:text-t2 border-l-3 border-transparent'
                      }
                    `}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    <span className="font-medium">{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      {/* Inline keyframes for slide animation */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
};

export default StoreNavigation;
